import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const cavalaireSurMerEn: ChauffeurPage = {
  slug: "cavalaire-sur-mer",
  title: "Private chauffeur & VTC in Cavalaire-sur-Mer",
  subtitle:
    "Private chauffeur in Cavalaire-sur-Mer: long sandy beach, marina, diving and transfers to Toulon-Hyères and Nice airports.",
  keywords: [
    "private chauffeur cavalaire-sur-mer",
    "vtc cavalaire",
    "cavalaire airport transfer",
    "chauffeur cavalaire marina",
  ],
  hero: {
    title: "Private chauffeur & VTC in Cavalaire-sur-Mer",
    subtitle:
      "The seaside resort at the gateway to the Massif des Maures: airport transfers, hourly hire and long distance, 7 days a week.",
    keywords: [
      "VTC Cavalaire",
      "Cavalaire marina chauffeur",
      "Toulon-Hyères airport transfer",
      "Gulf of Saint-Tropez chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur & VTC in Cavalaire-sur-Mer",
    badges: [
      "Toulon-Hyères airport ~45 min",
      "Flight tracking",
      "FR/EN chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Cavalaire-sur-Mer",
    items: [
      {
        title: "Two airports served",
        description:
          "Toulon-Hyères, the nearest at around 45 min, and Nice Côte d'Azur at close to 1h45: we pick the one that fits your flight.",
      },
      {
        title: "Gateway to the Maures",
        description:
          "The winding roads towards Le Rayol, La Croix-Valmer and the Gulf of Saint-Tropez are handled by the chauffeur.",
      },
      {
        title: "Marina & main beach",
        description:
          "Drop-off at the marina, along the promenade and at the diving clubs, with no scramble for parking in summer.",
      },
      {
        title: "Rated 5.0/5",
        description:
          "Based on the French Riviera, punctual and discreet, we hold 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Our chauffeur services in Cavalaire",
    services: [
      {
        title: "Toulon-Hyères airport transfer",
        text: "The shortest link, around 45 min, with flight tracking to and from Toulon-Hyères airport.",
      },
      {
        title: "Nice airport transfer",
        text: "Long-distance run to Nice Côte d'Azur, close to 1h45 by motorway, in a sedan or premium van.",
      },
      {
        title: "Toulon TGV station",
        text: "Shuttle to Toulon station for TGV trains to Paris and connections, departure set to your schedule.",
      },
      {
        title: "Hourly hire",
        text: "A dedicated chauffeur by the hour or by the day for the gulf, the beaches and the Maures villages.",
      },
      {
        title: "Long distance & Saint-Tropez",
        text: "To Saint-Tropez, Cannes, Monaco and Italy, or a run from Cannes and Nice down to Cavalaire.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Cavalaire marina",
        description: "Pleasure port and quays in the town centre.",
      },
      {
        name: "Main beach",
        description: "Long sandy beach and its seafront promenade.",
      },
      {
        name: "Cap Lardier & Cap Taillat",
        description: "Diving sites and coastal footpaths.",
      },
      {
        name: "La Croix-Valmer & Le Rayol",
        description: "Neighbouring villages and the Maures corniche.",
      },
      {
        name: "Toulon-Hyères airport",
        description: "The nearest airport, around 45 min away.",
      },
      {
        name: "Gulf of Saint-Tropez",
        description: "Saint-Tropez and Ramatuelle, about thirty minutes away.",
      },
    ],
  },
  generic: {
    heading: "Cavalaire-sur-Mer, between the gulf and the Maures",
    subheading:
      "A seaside resort a little off the main routes, where a chauffeur genuinely saves time.",
    paragraphs: [
      "Cavalaire-sur-Mer closes the Gulf of Saint-Tropez to the south and opens the Massif des Maures: fine but winding roads, and a summer crowd that quickly clogs the approaches to the marina and the main beach. A private chauffeur drops you as close as possible to your address and spares you the hunt for scarce parking from June to September.",
      "For arrivals, two airports frame the resort: Toulon-Hyères, the nearest at around forty-five minutes, and Nice Côte d'Azur, close to 1h45 by the A57. Depending on your flight, we set the departure time and track the aircraft for a wait-free welcome; Toulon's TGV station remains the most direct option to Paris.",
      "On the spot, hourly hire keeps the same chauffeur for your beach runs, your dinners in the gulf or your outings along the Maures corniche, with fixed fares agreed in advance and an instant quote.",
    ],
    bullets: [
      "Drop-off at the door",
      "Two airports to choose from",
      "Fixed fares & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Cavalaire",
    steps: [
      "Tell us your flight (Toulon-Hyères or Nice) or your address in Cavalaire.",
      "We recommend the vehicle to suit the distance, the luggage and the group.",
      "The chauffeur tracks the flight and sets the departure to smooth the arrival.",
      "Direct transfer or hourly hire for the beaches and the gulf.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Cavalaire-sur-Mer"),
    {
      q: "Which airport is closest to Cavalaire?",
      a: "Toulon-Hyères airport is the nearest, around 45 minutes away. The larger Nice Côte d'Azur airport is close to 1h45 by motorway.",
    },
    {
      q: "Do you handle transfers to Toulon TGV station?",
      a: "Yes. Toulon station serves TGV trains to Paris and connections; we set the departure to your train time, with a margin for the summer coastal traffic.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible from June to September, a very busy period around the gulf; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to the international clientele of the beaches and villas of the gulf.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "A Mercedes E-Class or S-Class sedan, a premium 7-seater van for groups and luggage, plus SUV and VIP minibus depending on the occasion.",
    },
    {
      q: "Do you serve Saint-Tropez and the nearby beaches?",
      a: "Yes, Saint-Tropez and Ramatuelle are about thirty minutes away; we also cover Le Rayol, La Croix-Valmer and the diving clubs, one-way or on hourly hire.",
    },
    {
      q: "Do you cover long distances from Cavalaire?",
      a: "Yes, any distance: Cannes, Nice, Monaco, Marseille and Italy, as well as runs from Cannes or Nice down to Cavalaire, in a comfortable vehicle with a dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "Private chauffeur in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    {
      label: "Book a chauffeur on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Airport transfers in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Short-term car rental in Cavalaire-sur-Mer",
      href: "/en/location/car-rental-cavalaire-sur-mer",
    },
  ],
  cta: {
    title: "Your private chauffeur in Cavalaire-sur-Mer",
    text: "Airport transfer, Toulon station or a day in the gulf: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book / request a quote",
  },
  seo: {
    title: "Car service Cavalaire — private chauffeur & transfers",
    description:
      "Private car service in Cavalaire-sur-Mer: transfers to Toulon-Hyères (~45 min) and Nice airports, Toulon TGV station, beaches and Gulf of Saint-Tropez.",
    canonical: "/en/chauffeur/cavalaire-sur-mer",
  },
};

export default cavalaireSurMerEn;
